<template>
  <div class="screenShareHolder" :class="[iAmMirroring ? 'iAmMirroring' : '']">
    <app-button
      button-class="screenShareButton"
      @real-click="handleScreenShareDebounced"
      @disabled-click="handleDisabledScreenShare"
      title="Share Screen"
      :disabled="!!noScreenShareReason"
      wobble="true"
    >
      <app-icon icon="share" />
      <span class="text" :class="[props.hideText ? 'visuallyHidden' : '']">
        <slot />
      </span>
    </app-button>
    <bubble-tip class="screenShareTip" name="noScreenShare" :show="showScreenShareMessage" :direction="tipDirection" @close="showScreenShareMessage = false">
      <span v-html="noScreenShareReason"></span>
    </bubble-tip>
  </div>
</template>
<script setup>
import { ref, computed, watch, getCurrentInstance } from 'vue';
import { storeToRefs } from 'pinia';
import _ from 'underscore';
import utils from '@/resources/utils';
import { localStorageKeysMap } from '@/shared/constants';
//import { usePageStore } from '@/store/pinia/page';
import { useMeetingStore } from '@/store/pinia/meeting';
import { usePhaseStore } from '@/store/pinia/phase';
import { useSidebarStore } from '@/store/pinia/sidebar';
import { useUsersStore } from '@/store/pinia/users';
import { useMediaStore } from '@/store/pinia/media';
import { useMessagesStore } from '@/store/pinia/messages';
import { useUiAnimations } from '@/composables/useUiAnimations';

import appIcon from '@/components/AppIcon.vue';
import appButton from '@/components/AppButton.vue';
import bubbleTip from '@/components/BubbleTip.vue';

const sidebarStore = useSidebarStore();
const meetingStore = useMeetingStore();
const usersStore = useUsersStore();
const phaseStore = usePhaseStore();
const mediaStore = useMediaStore();
const messagesStore = useMessagesStore();

const { $ee, $meetingmanager } = getCurrentInstance().appContext.config.globalProperties;
const { nopeWaggle } = useUiAnimations();

const props = defineProps({
  hideText: {
    default: false,
    type: Boolean,
  },
  tipDirection: {
    default: 'above',
    type: String,
  },
});

const showScreenShareMessage = ref(false);
const screenShareButton = ref(null);

const { screenSharing } = storeToRefs(mediaStore);
const { me, speaking, iAmSpeaking, allUsers, usersHere, iAmAwayInGroup, iAmMirroring, iAmSharingScreen, iAmInWaitingRoom } = storeToRefs(usersStore);
const { current: currentSidebar } = storeToRefs(sidebarStore);
const { currentPhase, settingUp, mirroring } = storeToRefs(phaseStore);
const { meetingId } = storeToRefs(meetingStore);

const noScreenShareReason = computed(() => {
  if (currentPhase.value === 'lobby') {
    return 'You can only share your screen once the meeting has started.';
  }

  if (iAmInWaitingRoom.value) {
    return "You can only share your screen once you've joined the meeting";
  }

  if (iAmMirroring.value || (currentPhase.value === 'input' && iAmSharingScreen.value)) {
    return 'You are already sharing your screen';
  }

  if (mirroring.value.requestedBy && mirroring.value.requestedBy !== me.value.id) {
    const presenter = allUsers.value.find((u) => u.id === mirroring.value.requestedBy);
    if (presenter) {
      return `${presenter.first_name} is currently sharing their screen.`;
    }
    return 'Screen share not allowed at the moment.';
  }
  if (settingUp.value || currentSidebar.value.barType === 'info') {
    return 'You cannot share your screen during phase setup.';
  }
  if (currentPhase.value === 'groups' && !iAmAwayInGroup.value) {
    return 'You cannot share your screen while finishing Breakout Groups.';
  }
  if (currentPhase.value === 'checkIn') {
    return 'You cannot share your screen during Check-In.';
  }
  if (currentPhase.value === 'checkOut') {
    return 'You cannot share your screen during Check-Out.';
  }
  if (currentPhase.value === 'breakTime') {
    return 'You cannot share your screen during&nbsp;break.';
  }
  if (currentPhase.value === 'endMeeting') {
    return 'You can only share your screen during the meeting.';
  }
  if (usersHere.value.length < 2) {
    return 'You cannot share your screen where there are no other&nbsp;users';
  }
  if (!['freeform', 'groups', 'dialogue'].includes(currentPhase.value) && !iAmSpeaking.value) {
    return `Only ${speaking.value?.first_name} can share their screen in this phase`;
  }

  return false;
});

watch(noScreenShareReason, (nv) => {
  if (!nv) {
    showScreenShareMessage.value = false;
  }
});

async function handleScreenShare() {
  if (noScreenShareReason.value) {
    handleDisabledScreenShare();
  } else {
    if (utils.isOpenedWithinElectronShell()) {
      const response = await ipcApi.send({ name: 'isScreenSharingPermissionGranted' });
      try {
        const parsed = JSON.parse(response);
        if (!parsed.isPermissionGranted) {
          messagesStore.addAlert('enableScreenRecording').catch(() => {});
          utils.storage.ls.setItem(localStorageKeysMap.RELAUNCH_MEETING_ID, meetingId.value);
          return;
        }
      } catch (e) {
        console.error('unable to verify screen sharing permission from MeetingButtons', e);
      }
    }
    if (currentPhase.value === 'input') {
      $ee.emit('bus:videoSourceChanged', screenSharing.value ? 'none' : 'screen');
      return;
    }
    if (mirroring.value.requestedBy) {
      $meetingmanager.stopMirroring({ mirroringId: mirroring.value.mirroringId });
    } else {
      $meetingmanager.startMirroring({ inputType: 'screen' });
    }
  }
}

const handleScreenShareDebounced = _.debounce(handleScreenShare, 1000);

function handleDisabledScreenShare() {
  if (noScreenShareReason.value) {
    nopeWaggle(screenShareButton.value, { duration: 0.8 });
    if (noScreenShareReason.value) {
      showScreenShareMessage.value = noScreenShareReason.value;
    }
  }
}
</script>
<style lang="scss" scoped>
:deep(button) {
  &:disabled {
    color: var(--c__disabled);
    @include triggered {
      color: var(--c__disabled);
    }
  }
}

.iAmMirroring :deep(button) {
  color: var(--c__accent);
  &:disabled {
    color: var(--c__accent);
  }
}
</style>
